import Header from '@/components/Header';
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { UserPlus, Building2, ShieldAlert, CalendarDays, Truck } from 'lucide-react';

export default function Team() {
  const { user, refreshMe } = useAuth();
  const isOwner = user?.org_role === 'owner';
  const canCreate = (user?.plan === 'business' || user?.role === 'admin') && !user?.org_id;
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [form, setForm] = useState({ email: '', full_name: '' });
  const [createForm, setCreateForm] = useState({ name: '', seat_cap: 5 });
  const [busy, setBusy] = useState(false);
  const [invited, setInvited] = useState(null);

  const load = async () => {
    const [o, m, s] = await Promise.all([
      api.get('/org'), api.get('/org/members'), api.get('/loads?scope=org').catch(() => ({ data: [] })),
    ]);
    setOrg(o.data); setMembers(m.data); setSchedule(s.data || []);
  };
  useEffect(() => { if (isOwner) load().catch(() => {}); }, [isOwner]);

  const createOrg = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post('/org/create', { name: createForm.name, seat_cap: Number(createForm.seat_cap) });
      toast.success('Organisation created'); await refreshMe?.();
    } catch (er) { toast.error(er?.response?.data?.detail || 'Could not create organisation'); }
    finally { setBusy(false); }
  };

  const invite = async (e) => {
    e.preventDefault(); setBusy(true); setInvited(null);
    try {
      const { data } = await api.post('/org/invite', form);
      setInvited(data); toast.success('Staff member added'); setForm({ email: '', full_name: '' }); load();
    } catch (er) { toast.error(er?.response?.data?.detail || 'Invite failed'); }
    finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email} from your organisation?`)) return;
    try { await api.delete(`/org/members/${m.id}`); toast.success('Removed'); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || 'Failed'); }
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-2xl mx-auto px-4 py-16">
          {canCreate ? (
            <form onSubmit={createOrg} className="card" data-testid="org-create">
              <Building2 className="w-10 h-10 text-teal-600 mb-3" />
              <h1 className="font-display font-black text-3xl mb-1">Create your organisation</h1>
              <p className="text-zinc-600 mb-5">Set up a team so you can invite staff, manage seats and share a load schedule.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2"><label className="label">Organisation name</label><input className="input" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} required data-testid="org-name" /></div>
                <div><label className="label">Seats</label><input type="number" min="2" max="1000" className="input" value={createForm.seat_cap} onChange={(e) => setCreateForm({ ...createForm, seat_cap: e.target.value })} data-testid="org-seats" /></div>
              </div>
              <button className="btn btn-primary mt-4" disabled={busy} data-testid="org-create-btn">{busy ? 'Creating…' : 'Create organisation'}</button>
            </form>
          ) : (
            <div className="text-center" data-testid="team-locked">
              <ShieldAlert className="w-12 h-12 mx-auto text-orange-500 mb-4" />
              <h1 className="font-display font-black text-3xl mb-2">Team management</h1>
              <p className="text-zinc-600">Team management is a <strong>Business plan</strong> feature. Upgrade to Business to create an organisation, invite staff and share a load schedule.</p>
              <Link to="/pricing" className="btn btn-primary mt-5">See Business plan</Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  const seatsUsed = members.length;
  const cap = org?.seat_cap || 0;
  const byDate = {};
  schedule.forEach((l) => { (byDate[l.date] = byDate[l.date] || []).push(l); });
  const dates = Object.keys(byDate).sort();

  return (
    <div className="min-h-screen"><Header />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center gap-3 mb-1"><Building2 className="w-6 h-6 text-teal-600" /><h1 className="font-display font-black text-3xl md:text-4xl">{org?.name || 'Your Team'}</h1></div>
        <p className="text-zinc-600 mb-6">Business plan · <strong>{seatsUsed}</strong> of <strong>{cap}</strong> seats used.</p>

        <form onSubmit={invite} className="card mb-6" data-testid="invite-form">
          <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><UserPlus className="w-4 h-4 text-teal-600" /> Add staff member</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div><label className="label">Email</label><input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="invite-email" /></div>
            <div><label className="label">Full name</label><input className="input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="invite-name" /></div>
            <div className="flex items-end"><button className="btn btn-primary w-full" disabled={busy || seatsUsed >= cap} data-testid="invite-btn">{busy ? 'Adding…' : 'Add member'}</button></div>
          </div>
          {seatsUsed >= cap && <p className="text-xs text-red-600 mt-2">Seat limit reached. Remove a member or ask your administrator for more seats.</p>}
          {invited && invited.email_sent && <div className="mt-3 p-3 rounded-lg bg-teal-50 border border-teal-200 text-sm" data-testid="invite-email-sent">A set-password email has been sent to <strong>{invited.email}</strong>. The link is valid for 24 hours.</div>}
          {invited && invited.existing && <div className="mt-3 p-3 rounded-lg bg-teal-50 border border-teal-200 text-sm">Existing user <strong>{invited.email}</strong> added to your organisation.</div>}
        </form>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-100 font-display font-bold">Members</div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500"><tr><th className="text-left px-4 py-3">Member</th><th className="text-left px-4 py-3">Role</th><th className="text-right px-4 py-3">Actions</th></tr></thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-t border-zinc-100" data-testid={`member-row-${m.id}`}>
                    <td className="px-4 py-3"><div className="font-semibold">{m.full_name || m.email.split('@')[0]}</div><div className="text-xs text-zinc-500">{m.email}</div></td>
                    <td className="px-4 py-3"><span className={`chip ${m.org_role === 'owner' ? 'chip-outline-orange' : 'chip-outline-yellow'}`}>{m.org_role || 'member'}</span></td>
                    <td className="px-4 py-3 text-right">{m.org_role !== 'owner' && <button onClick={() => remove(m)} className="text-xs font-semibold text-red-600 hover:underline" data-testid={`member-del-${m.id}`}>Remove</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card" data-testid="org-schedule">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><CalendarDays className="w-4 h-4 text-teal-600" /> Team load schedule</h3>
              <Link to="/loads" className="text-orange-600 text-sm font-semibold hover:underline">Open Loads →</Link>
            </div>
            {dates.length === 0 && <p className="text-sm text-zinc-500">No upcoming loads scheduled by your team yet.</p>}
            <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
              {dates.map((d) => (
                <div key={d} data-testid={`sched-date-${d}`}>
                  <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">{new Date(d + 'T00:00:00').toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                  <div className="space-y-1.5">
                    {byDate[d].map((l) => (
                      <div key={l.id} className="flex items-center gap-2 text-sm p-2 rounded-lg border border-zinc-100">
                        <Truck className="w-4 h-4 text-orange-500 shrink-0" />
                        <span className="font-medium truncate flex-1">{l.title}</span>
                        <span className="text-xs text-zinc-500 truncate hidden sm:block">{l.origin} → {l.destination}</span>
                        {(l.clearance_summary?.fail || 0) > 0 && <span className="chip text-[10px] bg-red-100 text-red-700 border border-red-200">{l.clearance_summary.fail}⛔</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
